import baseApi from 'shared/api/baseApi';

import type {
  ClonePayload,
  CloneResponse,
  CreateMasterPayload,
  CreateMasterResponse,
  CreateSubProjectPayload,
  InitializeMasterFunnelResponse,
  MasterAlertsPatch,
  MasterCapabilitiesPatch,
  MasterDetail,
  MasterIdentityPatch,
  MasterParametersPatch,
  MasterPhonePatch,
  ProjectDetail,
  ProjectPhoneConfig,
  UpdateMinPricesResponse,
  UpdateProjectPayload,
  UpdateStockAndPricesPayload,
  UpdateStockAndPricesResponse,
  UpdateWhatsAppHealthResponse,
} from './types';

const masterApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    createMaster: build.mutation<CreateMasterResponse, CreateMasterPayload>({
      query: (body) => ({
        url: '/backoffice/projects/master',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MasterProject', 'Subprojects'],
    }),
    getMasterWithSubs: build.query<MasterDetail, number>({
      query: (id) => ({ url: `/admin/projects/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'MasterProject', id }, 'Subprojects'],
    }),
    updateProject: build.mutation<MasterDetail, UpdateProjectPayload>({
      query: ({ id, patch }) => ({
        url: `/admin/projects/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id, patch }) => [
        { type: 'MasterProject', id },
        'MasterProject',
        'Subprojects',
        // Only a prompt change creates a version, and it is the only patch that can carry a model.
        ...(patch.prompt !== undefined || patch.commonPrompt !== undefined
          ? (['AgentModelOptions', 'PromptVersions'] as const)
          : []),
      ],
    }),
    updateMasterProject: build.mutation<MasterDetail, { id: number; patch: MasterIdentityPatch }>({
      query: ({ id, patch }) => ({
        url: `/backoffice/projects/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'MasterProject', id }, 'MasterProject', 'Subprojects'],
    }),
    updateMasterCapabilities: build.mutation<MasterDetail, { id: number; patch: MasterCapabilitiesPatch }>({
      query: ({ id, patch }) => ({
        url: `/backoffice/projects/${id}/capabilities`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'MasterProject', id }, 'MasterProject', 'Subprojects'],
    }),
    updateMasterParameters: build.mutation<MasterDetail, { id: number; patch: MasterParametersPatch }>({
      query: ({ id, patch }) => ({
        url: `/backoffice/projects/${id}/parameters`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'MasterProject', id }, 'MasterProject', 'Subprojects'],
    }),
    updateProjectPhone: build.mutation<MasterDetail, { id: number; patch: MasterPhonePatch }>({
      query: ({ id, patch }) => ({
        url: `/admin/projects/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'MasterProject', id }, 'MasterProject'],
    }),
    updateMasterAlerts: build.mutation<MasterDetail, { id: number; patch: MasterAlertsPatch }>({
      query: ({ id, patch }) => ({
        url: `/backoffice/projects/${id}/alerts`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'MasterProject', id }, 'MasterProject'],
    }),
    initializeMasterFunnel: build.mutation<InitializeMasterFunnelResponse, { projectId: number }>({
      query: (body) => ({
        url: '/admin/funnel/initialize',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'MasterProject', id: projectId }],
    }),
    getProjectPhoneConfig: build.query<ProjectPhoneConfig, number>({
      query: (projectId) => ({
        url: '/backoffice/d360/phone-config',
        params: { projectId },
      }),
      providesTags: (_result, _error, projectId) => [{ type: 'MasterProject', id: projectId }],
    }),
    lookupD360Channel: build.query<{ channelId: string; apiKey: string }, string>({
      query: (phoneNumber) => ({
        url: '/backoffice/d360/lookup',
        params: { phoneNumber },
      }),
    }),
    lookupProjectByPhone: build.query<
      { project: { id: number; name: string } | null },
      { phoneNumber: string; excludeProjectId: number }
    >({
      query: ({ phoneNumber, excludeProjectId }) => ({
        url: '/backoffice/d360/project-by-phone',
        params: { phoneNumber, excludeProjectId },
      }),
    }),
    configureD360Channel: build.mutation<
      { success: boolean },
      {
        projectId: number;
        phoneNumber: string;
        channelId: string;
        apiKey: string;
      }
    >({
      query: (body) => ({ url: '/backoffice/d360/configure', method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'MasterProject', id: projectId }, 'MasterProject'],
    }),
    configureD360Webhook: build.mutation<{ success: boolean }, { projectId: number }>({
      query: (body) => ({ url: '/backoffice/d360/configure-webhook', method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'MasterProject', id: projectId }, 'MasterProject'],
    }),
    // Re-reads quality rating and messaging tier from the provider (Meta Graph or the 360dialog partner
    // hub) for a single master project. Invalidating MasterProject refetches phone-config, which is
    // where the chips read the stored values from.
    refreshProjectWhatsAppHealth: build.mutation<UpdateWhatsAppHealthResponse, number>({
      query: (projectId) => ({
        url: '/templates/update-whatsAppHealth',
        method: 'POST',
        body: { projectId },
      }),
      invalidatesTags: (_result, _error, projectId) => [{ type: 'MasterProject', id: projectId }],
    }),
    cloneProject: build.mutation<CloneResponse, ClonePayload>({
      query: (body) => ({
        url: '/backoffice/projects/clone',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => (arg.dryRun ? [] : ['MasterProject', 'Subprojects']),
    }),
    createSubProject: build.mutation<ProjectDetail, CreateSubProjectPayload>({
      query: (body) => ({
        url: '/admin/projects',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { masterProjectId }) => [
        { type: 'MasterProject', id: masterProjectId },
        'MasterProject',
        'Subprojects',
      ],
    }),
    updateStockAndPrices: build.mutation<UpdateStockAndPricesResponse, UpdateStockAndPricesPayload>({
      query: ({ id, dryRun = false, forceUpdate = false }) => ({
        url: `/admin/projects/${id}/update-stock-and-prices`,
        method: 'POST',
        body: { dryRun, forceUpdate },
        // Backend returns structured step results on 422/502; treat as success so callers can read stockUpdate/minPricesUpdate.
        validateStatus: (response) => response.status === 200 || response.status === 422 || response.status === 502,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'MasterProject', id }, 'MasterProject', 'Subprojects'],
    }),
    updateMinPrices: build.mutation<UpdateMinPricesResponse, number>({
      query: (id) => ({
        url: `/admin/projects/${id}/update-min-prices`,
        method: 'POST',
        // Backend returns a structured minPricesUpdate on 502; treat as success so callers can read the error.
        validateStatus: (response) => response.status === 200 || response.status === 502,
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'MasterProject', id }, 'MasterProject', 'Subprojects'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateMasterMutation,
  useGetMasterWithSubsQuery,
  useGetProjectPhoneConfigQuery,
  useUpdateProjectMutation,
  useUpdateMasterProjectMutation,
  useUpdateMasterCapabilitiesMutation,
  useUpdateMasterParametersMutation,
  useUpdateMasterAlertsMutation,
  useUpdateProjectPhoneMutation,
  useInitializeMasterFunnelMutation,
  useLazyLookupD360ChannelQuery,
  useLazyLookupProjectByPhoneQuery,
  useConfigureD360ChannelMutation,
  useConfigureD360WebhookMutation,
  useRefreshProjectWhatsAppHealthMutation,
  useCloneProjectMutation,
  useCreateSubProjectMutation,
  useUpdateStockAndPricesMutation,
  useUpdateMinPricesMutation,
} = masterApi;

export default masterApi;
